import React, { useMemo } from 'react';
import { Chart } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarController,
  LineController,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  Plugin,
} from 'chart.js';
import { MONTH_NAMES } from '../constants';
import { useYearlyData } from '../hooks/useYearlyData';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarController,
  LineController,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface AnnualOverviewProps {
  refreshTrigger?: number;
}

interface BalanceDataset {
  label?: string;
  signedData?: number[];
}

const toFiniteNumber = (value: unknown): number | null => {
  const normalizedValue =
    typeof value === 'string' ? value.replace(/[,¥\s]/g, '') : value;
  const numericValue = Number(normalizedValue);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const balanceLabelPlugin: Plugin<'bar'> = {
  id: 'balanceLabelPlugin',
  afterDatasetsDraw: (chart) => {
    const balanceDatasetIndex = chart.data.datasets.findIndex(
      (dataset) => dataset.label === '差额'
    );

    if (balanceDatasetIndex === -1) {
      return;
    }

    const balanceDataset = chart.data.datasets[balanceDatasetIndex] as BalanceDataset;
    const signedData = balanceDataset.signedData ?? [];
    const { ctx } = chart;
    const meta = chart.getDatasetMeta(balanceDatasetIndex);

    ctx.save();
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';

    meta.data.forEach((element, index) => {
      const amount = signedData[index] ?? 0;
      const { x, y } = element.getProps(['x', 'y'], true);

      ctx.fillStyle = amount >= 0 ? 'rgba(22, 101, 52, 1)' : 'rgba(185, 28, 28, 1)';
      ctx.fillText(amount.toLocaleString(), x, y - 4);
    });

    ctx.restore();
  },
};

const AnnualOverview: React.FC<AnnualOverviewProps> = ({ refreshTrigger }) => {
  const currentYear = new Date().getFullYear();
  const { yearlyData, loading, refresh } = useYearlyData(currentYear);

  // Refresh when trigger changes
  React.useEffect(() => {
    if (refreshTrigger) {
      refresh();
    }
  }, [refreshTrigger, refresh]);

  // 使用 API 数据，失败时显示空数据
  const annualSpending = useMemo(() => {
    if (yearlyData?.monthly_summaries) {
      const spending = Array(12).fill(0);
      yearlyData.monthly_summaries.forEach((summary) => {
        const monthIndex = parseInt(summary.month_year.split('-')[1]) - 1;
        if (monthIndex >= 0 && monthIndex < 12) {
          spending[monthIndex] = Math.abs(toFiniteNumber(summary.total_expenses) ?? 0);
        }
      });
      return spending;
    }
    return Array(12).fill(0); // API 失败时显示空数据
  }, [yearlyData]);

  const annualIncome = useMemo(() => {
    if (yearlyData?.monthly_summaries) {
      const income = Array(12).fill(0);
      yearlyData.monthly_summaries.forEach((summary) => {
        const monthIndex = parseInt(summary.month_year.split('-')[1]) - 1;
        if (monthIndex >= 0 && monthIndex < 12) {
          income[monthIndex] = toFiniteNumber(summary.total_income) ?? 0;
        }
      });
      return income;
    }
    return Array(12).fill(0);
  }, [yearlyData]);

  const monthlyBalance = useMemo(() => {
    return annualIncome.map((income, index) => income - annualSpending[index]);
  }, [annualIncome, annualSpending]);
  const monthlyBalanceBars = useMemo(
    () => monthlyBalance.map((amount) => Math.abs(amount)),
    [monthlyBalance]
  );

  const yearlyTotalIncome =
    toFiniteNumber(yearlyData?.yearly_totals?.total_income) ??
    annualIncome.reduce((total, amount) => total + amount, 0);
  const yearlyTotalExpenses =
    Math.abs(toFiniteNumber(yearlyData?.yearly_totals?.total_expenses) ?? 0) ||
    annualSpending.reduce((total, amount) => total + amount, 0);
  const yearlyNetBalance = yearlyTotalIncome - yearlyTotalExpenses;

  const data = {
    labels: MONTH_NAMES,
    datasets: [
      {
        type: 'bar' as const,
        label: '差额',
        data: monthlyBalanceBars,
        signedData: monthlyBalance,
        backgroundColor: monthlyBalance.map((amount) =>
          amount >= 0 ? 'rgba(22, 163, 74, 0.55)' : 'rgba(220, 38, 38, 0.55)'
        ),
        borderColor: monthlyBalance.map((amount) =>
          amount >= 0 ? 'rgba(22, 163, 74, 1)' : 'rgba(220, 38, 38, 1)'
        ),
        borderWidth: 1,
        barThickness: 16,
        maxBarThickness: 18,
        categoryPercentage: 0.55,
        barPercentage: 0.7,
        order: 2,
      },
      {
        type: 'line' as const,
        label: '总收入',
        data: annualIncome,
        borderColor: 'rgba(22, 163, 74, 1)',
        backgroundColor: 'rgba(22, 163, 74, 0.14)',
        fill: false,
        tension: 0.3,
        pointRadius: 3,
        order: 1,
      },
      {
        type: 'line' as const,
        label: '总支出',
        data: annualSpending,
        borderColor: 'rgba(220, 38, 38, 1)',
        backgroundColor: 'rgba(220, 38, 38, 0.14)',
        fill: false,
        tension: 0.3,
        pointRadius: 3,
        order: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 20,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grace: '12%',
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            if (context.dataset.label === '差额') {
              return `差额: ${monthlyBalance[context.dataIndex].toLocaleString()}`;
            }

            return `${context.dataset.label}: ${Number(context.raw).toLocaleString()}`;
          },
        },
      },
    },
  };

  return (
    <section className="bg-white border border-gray-200 rounded-xl shadow-lg p-6">
      <h2 className="text-2xl font-semibold mb-4">年度概览</h2>
      <p className="text-gray-600 mb-6">
        跟踪您全年的收入、支出和差额趋势，识别每月现金流变化。
      </p>

      {loading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-700 mx-auto mb-2"></div>
          <p className="text-gray-600">加载年度数据中...</p>
        </div>
      )}



      <div className="mb-3 flex justify-center items-center  gap-x-5 gap-y-2 text-sm text-gray-700">
        <div className="flex items-center gap-2">
          <span className="h-0 w-8 border-t-2 border-green-600"></span>
          <span>收入</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="h-0 w-8 border-t-2 border-red-600"></span>
          <span>支出</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="h-4 w-3 rounded-sm border border-green-600 bg-green-500/55"></span>
          <span>结余</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="h-4 w-3 rounded-sm border border-red-600 bg-red-600/55"></span>
          <span>超支</span>
        </div>
      </div>

      <div className="relative h-96 max-h-[500px] max-w-4xl mx-auto">
        <Chart type="bar" data={data} options={options} plugins={[balanceLabelPlugin]} />
      </div>

      {yearlyData && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
          <div className="bg-gray-100 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-500">年度总收入</h3>
            <p className="text-2xl font-semibold text-green-600">
              ¥{yearlyTotalIncome.toLocaleString()}
            </p>
          </div>
          <div className="bg-gray-100 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-500">年度总支出</h3>
            <p className="text-2xl font-semibold text-red-600">
              ¥{Math.abs(yearlyTotalExpenses).toLocaleString()}
            </p>
          </div>
          <div className="bg-gray-100 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-500">年度差额</h3>
            <p
              className={`text-2xl font-semibold ${
                yearlyNetBalance >= 0
                  ? 'text-green-600'
                  : 'text-red-600'
              }`}
            >
              ¥{yearlyNetBalance.toLocaleString()}
            </p>
          </div>
        </div>
      )}
    </section>
  );
};

export default AnnualOverview;
